import React, { useContext, useEffect, useRef, useState } from "react";
import { IActivateBasketFullModel, IActivateGiftCardBillingDetails } from "../../../models/ActivateGiftCard.model";
import DataService from "../../../services/DataService.service";
import { AppSettingContext } from "../../Application";
import LoadingComponent from "../../shared/LoadingComponent";
import BillingDetailsStep from "./BillingDetailsStep";
import ChooseLoadAmountStep from "./ChooseLoadAmountStep";

interface ILoadGiftCardFormProps {
    currentStep: string;
    activationNumber: string;
    basketData: IActivateBasketFullModel;

    setBasketData(basketData: IActivateBasketFullModel): void;
    proceedToNextStep(): void;
    handleSetCurrentStep(): void;
}

const LoadGiftCardForm = (props: ILoadGiftCardFormProps) => {
    const { labels } = useContext(AppSettingContext);
    const firstRender = useRef(true);

    //#region STATE PROPERTIES

    const [isLoading, setIsLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [chooseAmount, setChooseAmount] = useState({
        loadAmount: props.basketData?.amountNoFees?.toString() ?? "",
        isValid: false
    });
    const [billingDetails, setBillingDetails] = useState({
        billingDetails: {
            firstName: props.basketData?.firstName ?? "",
            lastName: props.basketData?.lastName ?? "",
            email: props.basketData?.email ?? "",
            phone: props.basketData?.phone ?? "",
        } as IActivateGiftCardBillingDetails,
        isValid: false
    });
    const [error, setError] = useState("");

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (chooseAmount.isValid &&
            billingDetails.isValid
        ) {
            setIsFormValid(true);
            return;
        }

        setIsFormValid(false);
    }, [chooseAmount, billingDetails]);

    useEffect(() => {
        if (isFormValid && props.basketData && firstRender.current) {
            firstRender.current = false;
            handleSaveAndContinue();
        }
    }, [isFormValid]);

    //#endregion

    //#region HANDLERS

    async function handleSaveAndContinue() {
        setIsLoading(true);
        const response = await DataService.updateGiftCardBasket(props.activationNumber, chooseAmount.loadAmount);

        if (response) {
            const cacheResponse = await DataService.saveActivateGiftCardBasket({
                basketId: response.basketId ?? "",
                activationNo: response.activationNo ?? "",
                amountNoFees: response.amountNoFees ?? 0,
                fees: response.fees ?? 0,
                totalAmount: response.totalAmount ?? Number(chooseAmount.loadAmount),
                firstName: billingDetails.billingDetails.firstName,
                lastName: billingDetails.billingDetails.lastName,
                email: billingDetails.billingDetails.email,
                phone: billingDetails.billingDetails.phone
            }, null);

            if (cacheResponse.status.toLowerCase() !== "ok") {
                console.warn("Could not save activate basket in cache.");
            }

            props.setBasketData({
                basketId: response.basketId,
                activationNo: response.activationNo,
                amountNoFees: response.amountNoFees,
                fees: response.fees,
                totalAmount: response.totalAmount,
                firstName: billingDetails.billingDetails.firstName,
                lastName: billingDetails.billingDetails.lastName,
                email: billingDetails.billingDetails.email,
                phone: billingDetails.billingDetails.phone
            });

            props.proceedToNextStep();
        } else {
            console.error("Error updating uv giftcard.");
            setError("Error updating uv giftcard.");
        }

        setIsLoading(false);
    }

    //#endregion

    return (
        <div className="box">
            {isLoading ? (
                <LoadingComponent />
            ) : (
                <>
                    {props.currentStep.toLowerCase() === "activate" ? (
                        <h3>
                            {labels.get("GiftCard.App.ActivateLoadCard.Labels.LoadDetails")}
                        </h3>
                    ) : (
                        <>
                            <ChooseLoadAmountStep
                                cacheLoadAmount={props.basketData?.totalAmount}
                                handleSetLoadAmount={(loadAmount, isValid) => {
                                    setChooseAmount({ loadAmount, isValid });
                                }}
                                handleSetCurrentStep={() => props.handleSetCurrentStep()}
                            />

                            <hr style={{ marginTop: "18px" }} />
                            <BillingDetailsStep
                                cacheBasket={props.basketData}
                                handleSetBillingDetails={(billingDetails, isValid) => {
                                    setBillingDetails({ billingDetails, isValid });
                                }}
                                handleSetCurrentStep={() => props.handleSetCurrentStep()}
                            />

                            {error && (
                                <p
                                    className="react-input-error-message"
                                    style={{ textAlign: 'center' }}
                                >
                                    {error}
                                </p>
                            )}

                            <button
                                className="btn btn__primary btn__large btn__full_width large-fs"
                                style={{ marginTop: '0' }}
                                disabled={!isFormValid || props.currentStep !== 'load'}
                                onClick={handleSaveAndContinue}
                            >
                                {labels.get("GiftCard.App.Steps.Customise.Btn.Save")}
                            </button>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default LoadGiftCardForm;